import React from 'react';
import { NewModal } from 'lib/components';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { IHighlight } from 'lib/api/highlights/useQueryHighlights';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';

interface UpsertHighlightModalProps {
  highlight?: IHighlight | null;
  onClose: () => void;
  onSave: (data: { title: string; description: string }) => void;
  isLoading: boolean;
}

const HighlightSchema = Yup.object().shape({
  title: Yup.string()
    .min(2, 'Title is too short')
    .max(255, 'Title is too long')
    .required('Title is required'),
  description: Yup.string()
    .min(10, 'Description is too short')
    .max(255, 'Description is too long')
    .required('Description is required'),
});

export const UpsertHighlightModal = ({
  highlight,
  onClose,
  onSave,
  isLoading,
}: UpsertHighlightModalProps) => {
  const isEditMode = !!highlight;

  const initialValues = {
    title: highlight?.title || '',
    description: highlight?.description || '',
  };

  const headerText = isEditMode ? 'Edit Highlight' : 'Add New Highlight';
  const saveButtonText = isEditMode ? 'Save Changes' : 'Add Highlight';

  return (
    <NewModal
      headerText={headerText}
      closeModal={isLoading ? () => {} : onClose}
      style={{ content: { width: 592, maxWidth: 592 } }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={HighlightSchema}
        onSubmit={values => {
          onSave(values);
        }}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <Flex flexDirection='column' gap='10px'>
              <Flex flexDirection='column' gap='11px'>
                <FormikInputField
                  name='title'
                  label='Title:'
                  isRequired={false}
                />
                <FormikTextAreaField
                  name='description'
                  label='Highlight description:'
                  isRequired={false}
                  showCounter
                  maxLength={255}
                />
              </Flex>
              <Gap gap='8px' justifyContent='flex-end'>
                <Button
                  text='Discard'
                  onClick={onClose}
                  variant='secondary'
                  disabled={isLoading || isSubmitting}
                />
                <Button
                  type='submit'
                  text={saveButtonText}
                  variant='primary'
                  loading={isLoading || isSubmitting}
                  disabled={!dirty || !isValid || isLoading || isSubmitting}
                />
              </Gap>
            </Flex>
          </Form>
        )}
      </Formik>
    </NewModal>
  );
};
